import { useCallback, useEffect, useRef, useState } from 'react';
import {
  assetTypeSchema,
  imageCategorySchema,
  type Asset,
  type AssetType,
  type ImageCategory,
  type ProjectSummary,
} from '@gc/shared';
import { ApiError } from '../api/client.js';
import { archivarAsset, listarAssets, restaurarAsset, subirAsset } from '../api/assets.js';
import { listarProyectos } from '../api/projects.js';

type Carga =
  | { readonly tipo: 'cargando' }
  | { readonly tipo: 'listo'; readonly assets: Asset[]; readonly nextCursor: string | null }
  | { readonly tipo: 'error'; readonly mensaje: string };

const ETIQUETA_TIPO: Record<AssetType, string> = {
  IMAGE: 'Imagen',
  VIDEO: 'Vídeo',
  VOICE: 'Voz',
  MUSIC: 'Música',
  AUDIO: 'Audio',
  STORYBOARD: 'Storyboard',
  SCRIPT: 'Guion',
  RENDER: 'Render',
};

function tamañoLegible(bytes: string): string {
  const numero = Number(bytes);
  if (numero < 1024) return `${numero} B`;
  if (numero < 1024 * 1024) return `${(numero / 1024).toFixed(1)} KiB`;
  return `${(numero / (1024 * 1024)).toFixed(1)} MiB`;
}

/** Vista previa según el tipo real del archivo, servida por la ruta protegida. */
function Vista({ asset }: { asset: Asset }): React.JSX.Element {
  if (asset.type === 'IMAGE') {
    return (
      <img
        src={asset.contentUrl}
        alt={asset.filename}
        loading="lazy"
        style={{ width: '100%', height: '7rem', objectFit: 'cover', background: 'var(--fondo-hundido)' }}
      />
    );
  }
  if (asset.type === 'VIDEO' || asset.type === 'RENDER') {
    return <video src={asset.contentUrl} controls preload="metadata" style={{ width: '100%' }} />;
  }
  if (asset.type === 'AUDIO' || asset.type === 'MUSIC' || asset.type === 'VOICE') {
    return <audio src={asset.contentUrl} controls preload="metadata" style={{ width: '100%' }} />;
  }
  return <p style={{ margin: 0 }}>Sin vista previa</p>;
}

export function Biblioteca(): React.JSX.Element {
  const [carga, setCarga] = useState<Carga>({ tipo: 'cargando' });
  const [proyectos, setProyectos] = useState<ProjectSummary[]>([]);
  const [tipo, setTipo] = useState<AssetType | ''>('');
  const [texto, setTexto] = useState('');
  const [etiqueta, setEtiqueta] = useState('');
  const [verArchivados, setVerArchivados] = useState(false);

  const [proyectoDestino, setProyectoDestino] = useState('');
  const [categoria, setCategoria] = useState<ImageCategory>('misc');
  const [etiquetasSubida, setEtiquetasSubida] = useState('');
  const [progreso, setProgreso] = useState<number | null>(null);
  const [errorSubida, setErrorSubida] = useState<string | null>(null);
  const inputArchivo = useRef<HTMLInputElement>(null);

  const recargar = useCallback(async () => {
    try {
      const pagina = await listarAssets({
        ...(tipo === '' ? {} : { type: tipo }),
        ...(texto === '' ? {} : { q: texto }),
        ...(etiqueta === '' ? {} : { tag: etiqueta }),
        status: verArchivados ? 'ARCHIVED' : 'AVAILABLE',
        limit: 40,
      });
      setCarga({ tipo: 'listo', assets: pagina.data, nextCursor: pagina.nextCursor });
    } catch (causa) {
      setCarga({
        tipo: 'error',
        mensaje: causa instanceof ApiError ? causa.message : 'No se pudo cargar la biblioteca.',
      });
    }
  }, [tipo, texto, etiqueta, verArchivados]);

  useEffect(() => {
    setCarga({ tipo: 'cargando' });
    void recargar();
  }, [recargar]);

  useEffect(() => {
    // Subir exige un proyecto de origen: los archivos se organizan por proyecto.
    void listarProyectos({ limit: 50 }).then((pagina) => {
      setProyectos(pagina.data);
      setProyectoDestino((actual) => actual || (pagina.data[0]?.id ?? ''));
    });
  }, []);

  async function subir(archivo: File): Promise<void> {
    if (proyectoDestino === '') {
      setErrorSubida('Crea un proyecto antes de subir archivos.');
      return;
    }
    setErrorSubida(null);
    setProgreso(0);
    try {
      const etiquetas = etiquetasSubida
        .split(',')
        .map((valor) => valor.trim())
        .filter((valor) => valor.length > 0);
      await subirAsset(proyectoDestino, archivo, {
        category: categoria,
        tags: etiquetas,
        onProgreso: ({ enviados, total }) => setProgreso(Math.round((enviados / total) * 100)),
      });
      if (inputArchivo.current) inputArchivo.current.value = '';
      await recargar();
    } catch (causa) {
      setErrorSubida(causa instanceof ApiError ? causa.message : 'No se pudo subir el archivo.');
    } finally {
      setProgreso(null);
    }
  }

  async function alternarArchivado(asset: Asset): Promise<void> {
    try {
      if (asset.status === 'AVAILABLE') await archivarAsset(asset.id);
      else await restaurarAsset(asset.id);
      await recargar();
    } catch (causa) {
      setErrorSubida(causa instanceof ApiError ? causa.message : 'La operación falló.');
    }
  }

  return (
    <section>
      <h1>Biblioteca</h1>

      <h2>Subir archivo</h2>
      <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <label>
          Proyecto de origen
          <br />
          <select
            value={proyectoDestino}
            onChange={(evento) => setProyectoDestino(evento.target.value)}
          >
            {proyectos.length === 0 && <option value="">(no hay proyectos)</option>}
            {proyectos.map((proyecto) => (
              <option key={proyecto.id} value={proyecto.id}>
                {proyecto.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Categoría (imágenes)
          <br />
          <select
            value={categoria}
            onChange={(evento) => setCategoria(evento.target.value as ImageCategory)}
          >
            {imageCategorySchema.options.map((valor) => (
              <option key={valor} value={valor}>
                {valor}
              </option>
            ))}
          </select>
        </label>
        <label>
          Etiquetas (separadas por comas)
          <br />
          <input
            value={etiquetasSubida}
            onChange={(evento) => setEtiquetasSubida(evento.target.value)}
            placeholder="kioto, templo"
          />
        </label>
        <label>
          Archivo
          <br />
          <input
            ref={inputArchivo}
            type="file"
            accept="image/png,image/jpeg,image/webp,video/mp4,audio/mpeg,audio/wav,audio/aac,audio/mp4"
            disabled={progreso !== null}
            onChange={(evento) => {
              const archivo = evento.target.files?.[0];
              if (archivo) void subir(archivo);
            }}
          />
        </label>
      </div>

      {progreso !== null && (
        <p role="status">
          Subiendo… {progreso}%
          <progress value={progreso} max={100} style={{ marginLeft: '0.5rem' }} />
        </p>
      )}
      {errorSubida !== null && (
        <p role="alert" style={{ color: 'var(--error)' }}>
          {errorSubida}
        </p>
      )}
      <p>
        <small>
          Se admiten PNG, JPEG, WebP, MP4, MP3, WAV, AAC y M4A. El tipo se comprueba por el contenido
          del archivo, no por su extensión.
        </small>
      </p>

      <h2>Archivos</h2>
      <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <label>
          Tipo
          <br />
          <select value={tipo} onChange={(evento) => setTipo(evento.target.value as AssetType | '')}>
            <option value="">Todos</option>
            {assetTypeSchema.options.map((valor) => (
              <option key={valor} value={valor}>
                {ETIQUETA_TIPO[valor]}
              </option>
            ))}
          </select>
        </label>
        <label>
          Nombre contiene
          <br />
          <input value={texto} onChange={(evento) => setTexto(evento.target.value)} />
        </label>
        <label>
          Etiqueta
          <br />
          <input value={etiqueta} onChange={(evento) => setEtiqueta(evento.target.value)} />
        </label>
        <label>
          <input
            type="checkbox"
            checked={verArchivados}
            onChange={(evento) => setVerArchivados(evento.target.checked)}
          />{' '}
          Ver archivados
        </label>
      </div>

      {carga.tipo === 'cargando' && <p role="status">Cargando archivos…</p>}
      {carga.tipo === 'error' && <p role="alert">{carga.mensaje}</p>}
      {carga.tipo === 'listo' && carga.assets.length === 0 && (
        <p>No hay archivos que coincidan con el filtro.</p>
      )}

      {carga.tipo === 'listo' && carga.assets.length > 0 && (
        <ul
          style={{
            listStyle: 'none',
            padding: 0,
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(14rem, 1fr))',
            gap: '1rem',
          }}
        >
          {carga.assets.map((asset) => (
            <li
              key={asset.id}
              style={{ border: '1px solid var(--borde)', borderRadius: '6px', padding: '0.5rem' }}
            >
              <Vista asset={asset} />
              <p style={{ margin: '0.5rem 0 0', wordBreak: 'break-word' }}>
                <strong>{asset.filename}</strong>
              </p>
              <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--texto-suave)' }}>
                {ETIQUETA_TIPO[asset.type]} · {tamañoLegible(asset.sizeBytes)}
                {asset.metadata.width !== undefined &&
                  ` · ${asset.metadata.width}×${asset.metadata.height}`}
                {asset.metadata.durationMs !== undefined &&
                  ` · ${Math.round(asset.metadata.durationMs / 1000)} s`}
              </p>
              {asset.tags.length > 0 && (
                <p style={{ margin: '0.25rem 0 0', fontSize: '0.8rem' }}>{asset.tags.join(' · ')}</p>
              )}
              <p style={{ margin: '0.5rem 0 0', display: 'flex', gap: '0.5rem' }}>
                <a href={`${asset.contentUrl}?download=true`}>Descargar</a>
                <button type="button" onClick={() => void alternarArchivado(asset)}>
                  {asset.status === 'AVAILABLE' ? 'Archivar' : 'Restaurar'}
                </button>
              </p>
            </li>
          ))}
        </ul>
      )}

      {carga.tipo === 'listo' && carga.nextCursor !== null && (
        <p>
          <small>Hay más archivos. Afina los filtros para acotar el listado.</small>
        </p>
      )}
    </section>
  );
}
